import React from 'react';

export type ImageSize = 'small' | 'medium' | 'large' | number;

const SIZE_PRESETS = {
  small: 200,
  medium: 400,
  large: '100%',
} as const;

const getWidthStyle = (width: ImageSize | undefined): string | number => {
  if (width == null) {
    return 400;
  }
  if (typeof width === 'number') {
    return width;
  }
  return SIZE_PRESETS[width] || 400;
};

export interface ImageElementContainerProps {
  width: ImageSize | undefined;
  children: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  minHeight?: number;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  tabIndex?: number;
}

export const ImageElementContainer: React.FC<ImageElementContainerProps> = ({
  width,
  children,
  containerRef,
  minHeight,
  className,
  onClick,
  onBlur,
  onContextMenu,
  tabIndex,
}) => {
  const style: React.CSSProperties = { maxWidth: getWidthStyle(width) };
  if (minHeight !== undefined) {
    style.minHeight = minHeight;
  }
  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      onClick={onClick}
      onBlur={onBlur}
      onContextMenu={onContextMenu}
      tabIndex={tabIndex}
    >
      {children}
    </div>
  );
};

export interface UploadProgressBarProps {
  progress: number;
}

export const UploadProgressBar: React.FC<UploadProgressBarProps> = ({
  progress,
}) => (
  <div className='w-full max-w-[200px] h-1 bg-neutral-200 rounded-full mt-2 overflow-hidden'>
    <div
      className='h-full bg-blue-500 transition-all duration-200'
      style={{ width: `${progress}%` }}
    />
  </div>
);

interface ImageStateWrapperProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  width: ImageSize | undefined;
  children: React.ReactNode;
  borderClass?: string;
}

export const ImageStateWrapper: React.FC<ImageStateWrapperProps> = ({
  containerRef,
  width,
  children,
  borderClass = 'border-neutral-200 bg-neutral-50',
}) => (
  <ImageElementContainer
    containerRef={containerRef}
    width={width}
    minHeight={100}
    className={`relative inline-block rounded-lg border ${borderClass} overflow-hidden max-w-full`}
  >
    <div className='flex flex-col items-center justify-center h-full min-h-[100px] p-4'>
      {children}
    </div>
  </ImageElementContainer>
);
